import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Collapse,
  TablePagination,
  TablePaginationProps,
} from "@mui/material";
import { FC, useState } from "react";
import { toTitleCase } from "../utils/toTitleCase";
import { InspectedImage } from "../../components/Inspection/components/InspectedImage";

interface Props {
  data: Record<string, any>[];
  loading: boolean;
  isAccordion?: boolean;
}

export const DataTable: FC<Props> = ({ data, loading, isAccordion }) => {
  const noTableData = !data || data.length === 0;
  const columns = !noTableData ? Object.keys(data[0]) : [];
  const [expandedLogId, setExpandedLogId] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage: TablePaginationProps["onPageChange"] = (
    _,
    newPage
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage: React.ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <TableContainer component={Paper}>
      <Table
        sx={{
          "& .MuiTableCell-head": {
            padding: "8px",
          },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col}
                sx={{ fontWeight: "bold", fontSize: "14px", color: "#003366" }}
              >
                {toTitleCase(col)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress />
                </Box>
              </TableCell>
            </TableRow>
          ) : noTableData ? (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Typography align="center" p={2}>
                  No data available
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                const isExpanded = expandedLogId === row.logId;

                return (
                  <>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell key={col}>
                          <Box
                            sx={{
                              color: col === "logId" ? "#fbc02d" : "#003366",
                              cursor: col === "logId" ? "pointer" : "default",
                              fontSize: "12px",
                              fontWeight: 600,
                              display: "flex",
                              gap: "1rem",
                            }}
                            onClick={() =>
                              setExpandedLogId(isExpanded ? null : row[col])
                            }
                          >
                            {row[col]}
                            {col === "logId" &&
                              (isExpanded ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              ))}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>

                    {isExpanded && isAccordion && (
                      <TableRow>
                        <TableCell colSpan={columns.length}>
                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box
                              display="flex"
                              flexDirection="column"
                              p={2}
                              gap={0.5}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "12px",
                                  color: "#003366",
                                }}
                              >
                                Inspection Log For {row.logId}
                              </Typography>

                              <InspectedImage />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
};
